import { chosenSites } from "app/common/services/chosenSites";
import { CHART_COLORS } from "app/constants/ChartColors";
import {
    getPointY,
    translateSourceText,
} from "app/pages/website-analysis/traffic-sources/mmx/components/ChannelsOverviewBarChart/ChannelsOverviewBarChartConfig";
import { orderedTrafficSources } from "app/Shared/utils";

import type { DataFormat } from "../../../components/ChannelsOverviewBarChart/ChannelsOverviewBarChartContainerFormat";
import type { ISeries } from "../../../components/ChannelsOverviewBarChart/types";
import type { IBetaTrafficSources, IChannelsBetaData } from "../../types/types";

export const buildSeries = (
    data: IChannelsBetaData,
    domains: string[],
    dataFormat: DataFormat,
    isCompare: boolean,
): ISeries[] => {
    return domains.flatMap((domainName) => {
        const domainData = data[domainName];
        if (!domainData) {
            return [];
        }

        const { trafficSources, totalVisits } = domainData;

        return [
            {
                name: domainName,
                id: domainName,
                data: orderedTrafficSources
                    .filter((source) => source in trafficSources)
                    .map((sourceName) => ({
                        key: translateSourceText(sourceName),
                        y: getPointY(trafficSources[sourceName], totalVisits, dataFormat),
                        color: isCompare
                            ? chosenSites.getSiteColor(domainName)
                            : CHART_COLORS.trafficSourcesColorsBySourceMMX[sourceName],
                    })),
            },
        ];
    });
};
const isTrafficDataEmpty = (trafficSourcesByDomain: Record<string, IBetaTrafficSources>): boolean => {
    if (!trafficSourcesByDomain) {
        return true;
    }

    return !Object.values(trafficSourcesByDomain).some((domainData) =>
        Object.values(domainData ?? {}).some((val) => (val ?? 0) > 0),
    );
};

export const transformMmxBetaData = (mmxTrafficSources: {
    Total: Record<string, IBetaTrafficSources>;
}): IChannelsBetaData => {
    const trafficSourcesByDomain = mmxTrafficSources.Total;

    if (isTrafficDataEmpty(trafficSourcesByDomain)) {
        return {};
    }

    return Object.entries(trafficSourcesByDomain).reduce<IChannelsBetaData>((acc, [domain, trafficSources]) => {
        const totalVisits = Object.values(trafficSources ?? {}).reduce((sum, val) => sum + (val ?? 0), 0);

        const transformedTrafficSources = Object.entries(trafficSources ?? {}).reduce<IBetaTrafficSources>(
            (transformed, [channel, value]) => {
                transformed[channel as keyof IBetaTrafficSources] = value || 0;
                return transformed;
            },
            {} as IBetaTrafficSources,
        );

        // Only include domain if it has any traffic data
        if (Object.keys(transformedTrafficSources).length > 0) {
            acc[domain] = {
                trafficSources: transformedTrafficSources,
                totalVisits,
            };
        }

        return acc;
    }, {});
};
